import { Injectable } from '@angular/core';
import {
  BuilderConfig,
  CheckoutConfig,
  ContentfulConfig,
  FiltersConfig,
  LegacyConfig,
  LocalizationConfig,
  MaintenanceConfig,
  SamConfigContext,
  StoreConfig,
  StoreSearchConfig,
  FeatureFlagsConfig,
} from '@scalefast/config-angular';
import { SamContextV4 } from '@scalefast/sam';

/** These imports may fail when running tests and before the build target of the store (that depends on 'generate-config' target)
 * is executed and all the .json config files are generated.
 * If you are running tests ensure you provide all the needed config service mocks needed
 **/
import * as builderConfig from './generated/builder.config.json';
import * as checkoutConfig from './generated/checkout.config.json';
import * as contentfulConfig from './generated/contentful.config.json';
import * as filtersConfig from './generated/filters.config.json';
import * as legacyConfig from './generated/legacy.config.json';
import * as localizationConfig from './generated/localization.config.json';
import * as maintenanceConfig from './generated/maintenance.config.json';
import * as samConfig from './generated/sam.config.json';
import * as storeConfig from './generated/store.config.json';
import * as storeSpecificConfig from './generated/store-specific.config.json';
import * as featureFlagsConfig from './generated/feature-flags.config.json';

/**
 * This class initializes the @scalefast/config-angular configuration system it needs to be in the store project because the config files are in the store.
 * But this class should be only used directly by the @scalefast/config-angular package.
 */
@Injectable({
  providedIn: 'root',
})
export class ConfigLoaderService {
  getStoreConfig(): StoreConfig {
    return this.loadJSON(storeConfig) as StoreConfig;
  }

  getSamConfigContext(): SamConfigContext {
    return this.loadJSON(samConfig) as SamConfigContext;
  }

  getBuilderConfig(): BuilderConfig {
    return this.loadJSON(builderConfig);
  }

  getContentfulConfig(): ContentfulConfig {
    return this.loadJSON(contentfulConfig);
  }

  getStoreSearchConfig(): StoreSearchConfig {
    const samV4Config = this.getSamConfigContext().apiContext.v4;
    const storeConfigGenerated = this.getStoreConfig();
    const storeSearchConfig = this.createStoreSearchConfig(samV4Config, storeConfigGenerated);
    return this.loadJSON(storeSearchConfig);
  }
  createStoreSearchConfig(samV4Config: SamContextV4, storeConfigGenerated: StoreConfig): StoreSearchConfig {
    let env;
    if (samV4Config.environment === 'production') {
      env = 'prod';
    } else if (samV4Config.environment === 'development') {
      env = 'dev';
    } else {
      env = `${samV4Config.machineName}-local`;
    }
    const storeSearchConfig = {
      userID: samV4Config.userID,
      locale: samV4Config.locale,
      currency: storeConfigGenerated.currency || 'EUR',
      search_type: 'strict',
      env,
    };
    return this.loadJSON(storeSearchConfig);
  }

  getFiltersConfig(): FiltersConfig {
    return this.loadJSON(filtersConfig);
  }

  getCheckoutConfig(): CheckoutConfig {
    return this.loadJSON(checkoutConfig);
  }

  getLocalizationConfig(): LocalizationConfig {
    return this.loadJSON(localizationConfig);
  }

  getLegacyConfig(): LegacyConfig {
    return this.loadJSON(legacyConfig);
  }

  getFeatureFlagsConfig(): FeatureFlagsConfig {
    return this.loadJSON(featureFlagsConfig) as FeatureFlagsConfig;
  }

  /**
   * The Maintenance Config is coming from a mocked json, as we still don't have the final structure.
   * We know that it won't come from v4, as it is as store config. It will probably comes fro mthe CMS or another way.
   * For the moment, until we have the final data structure, we've created the type in config-angular
   * and the config is located in a maintenance-config.json
   */
  getMaintenanceConfig(): MaintenanceConfig {
    return this.loadJSON(maintenanceConfig);
  }

  getStoreSpecificConfig(): Record<string, any> {
    return this.loadJSON(storeSpecificConfig);
  }

  private loadJSON<T>(config: T): T {
    /**
     * There is a problem resolving JSON modules in the browser that loads the json
     * with a root "default" property, this is a workaround
     * @TODO spend more time with tsconfig options, import statements and webpack compilation
     * and checking generated code to know if there is a better solution
     */
    const checkDefault = config as any;
    if (checkDefault.default) {
      return checkDefault.default;
    }
    return config;
  }
}
